import {
  distanceUnitByUnitType,
  elevationUnitByUnitType,
} from '@racemap/utilities/functions/units';
import { clamp } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { getRenderInfoBuffer, type RenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import { getHiddenIds, getSelectedIds } from 'src/lib/player/selectors';
import { getStarterInfos, type StarterInfo } from 'src/lib/player/statistics';
import { type StarterType, useStore } from 'src/lib/player/store';
import {
  convertElevationUnitToMeter,
  convertMeterToElevationUnit,
  distanceUnitToMeterFactor,
  meanFilter,
  meterToDistanceUnitFactor,
} from 'src/lib/player/utils';
import { useBaseColor } from 'src/lib/player/utils/baseColor';
import { useMeasure } from 'src/lib/player/utils/hooks';
import tinycolor from 'tinycolor2';
import { findGridLinesDistance, findGridLinesElevation } from '../InfoSheet/gridlines';
import SVGChart, { type Bounds } from '../InfoSheet/SVGChart';
import { HeightChartCrosshair } from './HeightChartCrosshair';
import { HeightChartPoi } from './HeightChartPoi';
import { HeightChartPolyline } from './HeightChartPolyline';
import { HeightChartProgressPolyline } from './HeightChartProgressPolyline';
import { HeightChartStarter } from './HeightChartStarter';
import { HeightChartStarterDots } from './HeightChartStarterDots';
import { chartDataFromShadowTrack, computeViewRange, type ZoomRange } from './heightChartUtils';
import { useHeightChartGestures } from './useHeightChartGestures';

type HeightChartProps = {
  height: number;
  width: number;
};

export default function HeightChart({ width, height }: HeightChartProps): JSX.Element {
  const toggleSelectedStarter = useStore((state) => state.actions.toggleSelectedStarter);
  const setHighlighted = useStore((state) => state.actions.setHighlighted);
  const setHighlightedOffset = useStore((state) => state.actions.setHighlightedOffset);
  const elevationChartOptions = useStore(
    (state) => state.events[state.currentEventIndices[0]].options.elevationChartOptions,
  );
  const unitType = useStore((state) => state.options.unitType);
  const currentEvent1 = useStore((state) => state.events[state.currentEventIndices[0]]);
  const track = currentEvent1.chunkedShadowTrack;
  const renderInfoBuffer = useStore(getRenderInfoBuffer);
  const highlightedId = useStore((state) => state.timer.highlighted);
  const selectedIds = useStore((state) => getSelectedIds(state.timer));
  const hiddenIds = useStore((state) => getHiddenIds(state.timer));
  const highlightedOffset = useStore((state) => state.timer.highlightedOffset);
  const pois = useStore((state) => state.events[state.currentEventIndices[0]].pois);
  const elevationShowProgress = useStore((state) => state.options.elevationShowProgress);
  const elevationShowGrid = useStore((state) => state.options.elevationShowGrid);
  const elevationShowAxis = useStore((state) => state.options.elevationShowAxis);
  const elevationShowPOI = useStore((state) => state.options.elevationShowPOI);

  const starterInfos = useStore((state) =>
    state.timer.selected
      .flatMap((selectedId) => getStarterInfos(state, selectedId))
      .filter(
        (starterInfo) =>
          !hiddenIds.has(starterInfo.id) &&
          starterInfo.starter.eventIndex === state.currentEventIndices[0],
      ),
  );
  const [backgroundColor] = useBaseColor();
  const isLightBackground = tinycolor(backgroundColor).isLight();
  const lineColor = isLightBackground ? 'black' : 'white';
  const starterInfoIdSet = useMemo(() => new Set(starterInfos.map((s) => s.id)), [starterInfos]);
  const poiIdSet = useMemo(() => new Set(pois.map((p) => p.id)), [pois]);
  const renderInfoByStarterId = useMemo(
    () => getRenderInfoByStarterId(starterInfos, renderInfoBuffer),
    [renderInfoBuffer],
  );

  function isStarter(id: string | null) {
    return id != null && starterInfoIdSet.has(id);
  }
  function isPoi(id: string | null) {
    return id != null && poiIdSet.has(id);
  }

  const [chartRef, chartBounds] = useMeasure({ debounce: 100, scroll: false });
  const boundsRef = useRef<Bounds | null>(null);
  const [zoom, setZoom] = useState<ZoomRange | null>(null);

  const highlightedStarterInfo = (() => {
    if (highlightedId == null || !starterInfoIdSet.has(highlightedId)) return null;
    const renderInfo = renderInfoByStarterId.get(highlightedId);
    if (!renderInfo) return null;
    return renderInfo;
  })();
  const leadingStarterInfo = (() => {
    const selectedStarterInfos = starterInfos.filter((s) => selectedIds.has(s.id));
    if (selectedStarterInfos.length === 0) return null;
    return selectedStarterInfos.reduce((prev, curr) => (prev.offset > curr.offset ? prev : curr));
  })();

  const coarseWidth = Math.round(width / 100) * 100;
  const filteredTrack = useMemo(() => {
    const fullTrack = Array.from(chartDataFromShadowTrack(track));
    const requestedPointCount = Math.round(coarseWidth / 3);
    return Array.from(meanFilter(fullTrack, clamp(requestedPointCount, 200, 1000)));
  }, [track, coarseWidth]);

  const viewRange = useMemo(
    () => computeViewRange(filteredTrack, unitType, elevationChartOptions, zoom),
    [filteredTrack, unitType, elevationChartOptions, zoom],
  );
  const dataMaxX = useMemo(
    () => filteredTrack.reduce((max, point) => Math.max(max, point.x), 0),
    [filteredTrack],
  );

  const bind = useHeightChartGestures({
    chartBounds,
    boundsRef,
    renderInfoBuffer,
    viewRange,
    dataMaxX,
    isPoiHighlighted: isPoi(highlightedId),
    setHighlightedOffset,
    setHighlighted,
    toggleSelectedStarter,
    setZoom,
  });

  const xAxis = {
    min: viewRange.minX,
    max: viewRange.maxX,
    ticks: findGridLinesDistance(
      viewRange.minX * meterToDistanceUnitFactor(unitType),
      viewRange.maxX * meterToDistanceUnitFactor(unitType),
      clamp(Math.round(width / 100), 5, 15),
    ).map((distanceInKilometer): [number, string] => [
      distanceInKilometer * distanceUnitToMeterFactor(unitType),
      `${Math.round(distanceInKilometer * 100) / 100} ${distanceUnitByUnitType(unitType)}`,
    ]),
  };
  const yAxis = {
    min: viewRange.minY,
    max: viewRange.maxY,
    ticks: findGridLinesElevation(
      viewRange.minY * convertMeterToElevationUnit(unitType),
      viewRange.maxY * convertMeterToElevationUnit(unitType),
      clamp(Math.round(height / 50), 3, 10),
    ).map((elevationInMeter): [number, string] => [
      elevationInMeter * convertElevationUnitToMeter(unitType),
      `${Math.round(elevationInMeter)} ${elevationUnitByUnitType(unitType)}`,
    ]),
  };

  return (
    <SVGChart
      width={width}
      height={height - 13}
      style={{ marginTop: 12 }}
      xAxis={xAxis}
      yAxis={yAxis}
      gestureBind={bind()}
      dimAxis={highlightedOffset !== -1}
      forwardRef={chartRef}
      boundsRef={boundsRef}
      lineColor={lineColor}
      showGrid={elevationShowGrid ?? undefined}
      showAxis={elevationShowAxis ?? undefined}
      onDoubleClick={() => setZoom(null)}
    >
      {(bounds) => {
        return (
          <>
            {highlightedOffset !== -1 && track != null && (
              <HeightChartCrosshair
                bounds={bounds}
                unitType={unitType}
                highlightedOffset={highlightedOffset}
                track={track}
                filteredTrack={filteredTrack}
                viewRange={viewRange}
                lineColor={lineColor}
              />
            )}
            {starterInfos.map((starterInfo) => {
              const starterOffset =
                renderInfoByStarterId.get(starterInfo.id)?.offset ?? starterInfo.offset;
              return (
                <HeightChartStarter
                  key={`starterline-${starterInfo.id}`}
                  starterOffset={starterOffset}
                  starter={starterInfo.starter}
                  bounds={bounds}
                  viewRange={viewRange}
                  filteredTrack={filteredTrack}
                  lineColor={lineColor}
                />
              );
            })}
            {pois
              .filter((poi) => !hiddenIds.has(poi.id))
              .map(
                (poi) =>
                  (elevationShowPOI ?? true) &&
                  poi.offset != null && (
                    <HeightChartPoi
                      key={`poi-${poi.id}`}
                      poi={poi}
                      bounds={bounds}
                      viewRange={viewRange}
                      filteredTrack={filteredTrack}
                      isSelected={selectedIds.has(poi.id)}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleSelectedStarter(poi.id);
                      }}
                      onHover={(event) => {
                        if (!isStarter(highlightedId)) {
                          event.stopPropagation();
                          setHighlighted(poi.id);
                        }
                      }}
                      onHoverOut={(event) => {
                        if (!isStarter(highlightedId)) {
                          event.stopPropagation();
                          setHighlighted(null);
                        }
                      }}
                    />
                  ),
              )}
            {highlightedStarterInfo != null && highlightedStarterInfo.starter != null && (
              <HeightChartStarter
                key={`starterline-${highlightedStarterInfo.starter.id}`}
                starterOffset={highlightedStarterInfo.offset}
                starter={highlightedStarterInfo.starter}
                bounds={bounds}
                viewRange={viewRange}
                filteredTrack={filteredTrack}
                lineColor={lineColor}
              />
            )}
            <HeightChartPolyline
              filteredTrack={filteredTrack}
              viewRange={viewRange}
              bounds={bounds}
              lineColor={lineColor}
            />
            {elevationShowProgress && leadingStarterInfo && (
              <HeightChartProgressPolyline
                filteredTrack={filteredTrack}
                viewRange={viewRange}
                bounds={bounds}
                progress={leadingStarterInfo.offset}
                lineColor={lineColor}
              />
            )}
            <HeightChartStarterDots
              renderInfoBuffer={renderInfoBuffer}
              renderInfoOffset={renderInfoBuffer.renderIndexOffsets.get(currentEvent1.index) || 0}
              renderInfoCount={currentEvent1.starters.length}
              bounds={bounds}
              viewRange={viewRange}
              filteredTrack={filteredTrack}
            />
          </>
        );
      }}
    </SVGChart>
  );
}

const getRenderInfoByStarterId = (
  starterInfos: Array<StarterInfo>,
  renderInfoBuffer: RenderInfoBuffer,
): Map<string, { starter: StarterType | null; offset: number }> => {
  return new Map(
    starterInfos.map((starterInfo) => {
      const renderIndex = renderInfoBuffer.starters.findIndex((s) => s.id === starterInfo.id);
      const renderOffset = renderIndex >= 0 ? renderInfoBuffer.getOffset(renderIndex) : -1;
      return [
        starterInfo.id,
        {
          starter: renderInfoBuffer.starters[renderIndex],
          offset:
            !Number.isNaN(renderOffset) && renderOffset > 0 ? renderOffset : starterInfo.offset,
        },
      ];
    }),
  );
};
